import { z } from 'zod';
import { useState } from 'react';
import { toast } from 'sonner';
import { Navigate, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@features/auth';
import { useAbility } from '@features/authorization';
import {
  ArticleList,
  ArticleDetail,
  ArticleEditor,
  articleSearchSchema,
  type ArticleSearch,
} from '@features/helpdesk-articles';
import { helpdeskSearchSchema } from '@features/helpdesk';
import { FaqUpdateDialog } from '@features/helpdesk-faq';
import { articlesApi } from '@shared/api';
import {
  canAccessHelpdesk,
  canAccessCrmCustomerDirectory,
  canAccessCrmCustomerMembershipDirectory,
} from '@shared/lib';
import { PageBackLink } from '@shared/ui';
import { CrmPrimaryNavigation } from './CrmPrimaryNavigation';
import { CrmKnowledgeNavigation } from './CrmKnowledgeNavigation';

export const articleRouteSearchSchema = helpdeskSearchSchema
  .extend(articleSearchSchema.shape)
  .extend({ edit: z.literal('1').optional(), articleTaskUuid: z.uuid().optional() });
type Search = ReturnType<typeof articleRouteSearchSchema.parse>;

export function CrmArticlesPage({
  locale,
  search,
  mode = 'list',
  articleId,
}: {
  locale: string;
  search: Search;
  mode?: 'list' | 'detail' | 'create';
  articleId?: string;
}) {
  const { t } = useTranslation();
  const ability = useAbility();
  const { user, permissions } = useAuth();
  const navigate = useNavigate();
  const scope = user ? `${user.id}:${JSON.stringify(permissions ?? [])}` : '';
  const [faqSession, setFaqSession] = useState<string | null>(null);
  const canRead = ability.can('read', 'HelpdeskArticle');
  const options = useQuery({
    queryKey: ['helpdesk-articles', scope, 'topics'],
    queryFn: ({ signal }) => articlesApi.topics(signal),
    enabled: canRead,
    retry: false,
  });
  const topics = {
    topics: options.data?.data.topics ?? [],
    isPending: options.isPending,
    error: options.error,
    onRetry: () => {
      void options.refetch();
    },
  };
  if (!canRead || (mode === 'create' && !ability.can('create', 'HelpdeskArticle')))
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  const back = () => {
    if (search.articleTaskUuid) {
      void navigate({
        to: '/$locale/crm/helpdesk/$taskUuid',
        params: { locale, taskUuid: search.articleTaskUuid },
        search: helpdeskSearchSchema.parse(search),
      });
      return;
    }
    void navigate({
      to: '/$locale/crm/helpdesk/articles',
      params: { locale },
      search: { ...search, edit: undefined },
    });
  };
  const open = (id: string, edit?: '1') => {
    void navigate({
      to: '/$locale/crm/helpdesk/articles/$articleId',
      params: { locale, articleId: id },
      search: { ...search, edit },
    });
  };
  const changeSearch = (next: ArticleSearch) => {
    void navigate({
      to: '/$locale/crm/helpdesk/articles',
      params: { locale },
      search: { ...search, ...next },
      replace: true,
      resetScroll: false,
    });
  };
  return (
    <main className="flex min-w-0 flex-col gap-4 p-4 pb-24 sm:p-6 sm:pb-24">
      {mode === 'list' ? (
        <CrmPrimaryNavigation
          className="mb-0"
          activeSection="helpdesk"
          locale={locale}
          canAccessHelpdesk={canAccessHelpdesk(ability)}
          canAccessProfiles={canAccessCrmCustomerDirectory(ability)}
          canAccessMemberships={canAccessCrmCustomerMembershipDirectory(ability)}
        />
      ) : null}
      {mode === 'list' && canAccessHelpdesk(ability) ? (
        <PageBackLink
          onClick={() => {
            void navigate({
              to: '/$locale/crm/helpdesk',
              params: { locale },
              search: helpdeskSearchSchema.parse(search),
            });
          }}
        >
          {t('articles.backRequests')}
        </PageBackLink>
      ) : null}
      {mode === 'list' ? (
        <CrmKnowledgeNavigation locale={locale} active="articles" search={search} />
      ) : null}
      {mode === 'list' ? (
        <ArticleList
          key={scope}
          scope={scope}
          search={search}
          topics={topics}
          onUpdateFaq={
            ability.can('read', 'EducationModule') && ability.can('update', 'EducationModule')
              ? () => {
                  setFaqSession(crypto.randomUUID());
                }
              : undefined
          }
          onSearchChange={changeSearch}
          onOpen={open}
          onOpenRelatedTask={
            canAccessHelpdesk(ability)
              ? (taskUuid) => {
                  void navigate({
                    to: '/$locale/crm/helpdesk/$taskUuid',
                    params: { locale, taskUuid },
                    search: helpdeskSearchSchema.parse(search),
                  });
                }
              : undefined
          }
          onEdit={
            ability.can('update', 'HelpdeskArticle')
              ? (id) => {
                  open(id, '1');
                }
              : undefined
          }
          canDelete={ability.can('delete', 'HelpdeskArticle')}
          onCreate={
            ability.can('create', 'HelpdeskArticle')
              ? () => {
                  void navigate({
                    to: '/$locale/crm/helpdesk/articles/new',
                    params: { locale },
                    search,
                  });
                }
              : undefined
          }
        />
      ) : mode === 'create' ? (
        <ArticleEditor
          key={scope}
          scope={scope}
          topics={topics}
          onCancel={back}
          onBack={back}
          onSaved={(detail) => {
            open(detail.data.id);
          }}
        />
      ) : articleId ? (
        <ArticleDetail
          key={`${user?.id ?? ''}:${articleId}`}
          scope={scope}
          id={articleId}
          topics={topics}
          canUpdate={ability.can('update', 'HelpdeskArticle')}
          canDelete={ability.can('delete', 'HelpdeskArticle')}
          onBack={back}
          startInEditMode={search.edit === '1'}
          onEditEntryConsumed={() => {
            void navigate({
              to: '/$locale/crm/helpdesk/articles/$articleId',
              params: { locale, articleId },
              search: { ...search, edit: undefined },
              replace: true,
            });
          }}
        />
      ) : null}
      {mode === 'list' && faqSession ? (
        <FaqUpdateDialog
          key={`${scope}:${faqSession}`}
          scope={scope}
          onClose={() => {
            setFaqSession(null);
          }}
          onSaved={() => {
            setFaqSession((current) => (current === faqSession ? null : current));
            toast.success(t('faq.saved'));
          }}
        />
      ) : null}
    </main>
  );
}
